// src/components/NewsletterModal.js
// Site-wide newsletter popup — branded to match the rest of the admin/public
// design system instead of a plain Bootstrap dialog.
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaPaperPlane, FaTimes, FaCheckCircle, FaBellSlash } from 'react-icons/fa';
import { subscribeToNewsletter } from '../utils/newsletterApi';

const MotionDiv = motion.div;

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const panelVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, y: 16, scale: 0.97, transition: { duration: 0.2, ease: 'easeIn' } },
};

function NewsletterModal({ show, onClose }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (show) {
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 350);
      return () => clearTimeout(t);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('submitting');
    setMessage('');
    try {
      await subscribeToNewsletter(email.trim(), 'popup');
      setStatus('success');
      try {
        localStorage.setItem('ibg_newsletter_subscribed', '1');
      } catch {
        // localStorage unavailable (e.g. private browsing) — safe to ignore.
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem('ibg_newsletter_dismissed_at', String(Date.now()));
    } catch {
      // localStorage unavailable — the popup will simply show again next visit.
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {show && (
        <MotionDiv
          className="d-flex align-items-center justify-content-center"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(8, 15, 34, 0.55)',
            backdropFilter: 'blur(6px)',
            zIndex: 1050,
            padding: '1.5rem',
          }}
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="dialog"
          aria-modal="true"
          aria-label="Newsletter subscription"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleDismiss();
          }}
        >
          <MotionDiv
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              width: '100%',
              maxWidth: 440,
              borderRadius: 24,
              overflow: 'hidden',
              background: 'var(--ibg-white)',
              boxShadow: '0 30px 80px rgba(8, 15, 34, 0.35)',
              position: 'relative',
            }}
          >
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Close"
              className="btn p-0 d-inline-flex align-items-center justify-content-center"
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.14)',
                color: 'var(--ibg-white)',
                border: 'none',
                zIndex: 2,
              }}
            >
              <FaTimes size={13} />
            </button>

            <div
              className="position-relative"
              style={{
                padding: '2.25rem 2rem 2rem',
                background: 'linear-gradient(150deg, rgba(3,17,43,0.97) 0%, rgba(7,38,94,0.94) 45%, rgba(9,66,150,0.90) 100%)',
                textAlign: 'center',
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'radial-gradient(circle at 20% 10%, rgba(102,178,255,0.20), transparent 30%), radial-gradient(circle at 85% 85%, rgba(13,110,253,0.24), transparent 32%)',
                  pointerEvents: 'none',
                }}
              />
              <div
                aria-hidden="true"
                className="d-inline-flex align-items-center justify-content-center mb-3 position-relative"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: 'var(--ibg-blue-soft, #66b2ff)',
                  fontSize: '1.4rem',
                }}
              >
                <FaPaperPlane />
              </div>
              <h4 className="fw-bold text-white mb-2 position-relative" style={{ letterSpacing: '-0.02em' }}>
                Stay in the loop
              </h4>
              <p className="mb-0 position-relative" style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                Get IBG programme updates, events, and banking insights delivered straight to your inbox.
              </p>
            </div>

            <div className="p-4 p-sm-4" style={{ paddingTop: '1.75rem' }}>
              {status === 'success' ? (
                <div className="text-center py-2">
                  <div
                    aria-hidden="true"
                    className="d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(25,135,84,0.10)', color: '#198754', fontSize: '1.4rem' }}
                  >
                    <FaCheckCircle />
                  </div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--ibg-ink)' }}>
                    You're subscribed!
                  </h5>
                  <p className="mb-3" style={{ color: 'var(--ibg-slate)' }}>
                    Thanks for staying connected with IBG.
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn fw-semibold"
                    style={{
                      borderRadius: 999,
                      border: 'none',
                      padding: '0.6rem 1.6rem',
                      background: 'linear-gradient(135deg, var(--ibg-navy) 0%, var(--ibg-blue) 100%)',
                      color: 'var(--ibg-white)',
                    }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <label className="visually-hidden" htmlFor="newsletter-modal-email">
                    Email address
                  </label>
                  <input
                    ref={inputRef}
                    id="newsletter-modal-email"
                    type="email"
                    required
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%',
                      borderRadius: 14,
                      minHeight: 52,
                      border: '1px solid var(--ibg-border)',
                      padding: '0.6rem 1rem',
                      marginBottom: '0.85rem',
                      fontSize: '0.95rem',
                    }}
                  />
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="d-flex align-items-center justify-content-center gap-2 w-100 fw-bold"
                    style={{
                      borderRadius: 14,
                      minHeight: 52,
                      border: 'none',
                      background: 'linear-gradient(135deg, var(--ibg-navy) 0%, var(--ibg-blue) 100%)',
                      color: 'var(--ibg-white)',
                      boxShadow: '0 14px 34px rgba(13,110,253,0.24)',
                    }}
                  >
                    {status === 'submitting' ? 'Subscribing...' : 'Subscribe'} {status !== 'submitting' && <FaPaperPlane size={13} />}
                  </button>

                  {status === 'error' ? (
                    <div className="mt-3 text-center" style={{ fontSize: '0.88rem', color: '#c0392b' }} role="alert">
                      {message}
                    </div>
                  ) : (
                    <div className="mt-3 text-center" style={{ fontSize: '0.85rem', color: 'var(--ibg-slate-soft)' }}>
                      Relevant updates. No noise. Unsubscribe anytime.
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleDismiss}
                    className="btn d-inline-flex align-items-center gap-2 w-100 justify-content-center mt-3"
                    style={{ border: 'none', background: 'transparent', color: 'var(--ibg-slate)', fontSize: '0.85rem', fontWeight: 600 }}
                  >
                    <FaBellSlash size={11} /> Maybe later
                  </button>
                </form>
              )}
            </div>
          </MotionDiv>
        </MotionDiv>
      )}
    </AnimatePresence>
  );
}

export default NewsletterModal;
